import { LOCALES, type Locale, localeChain } from '@cloud/shared/i18n/locale'

/**
 * Het meegeleverde woordenboek waarop een taal staat: de eerste code uit de keten die we
 * meeleveren. Een variant (`nl-BE`, een eigen tenanttaal) erft zo het Nederlands en legt
 * alleen zijn eigen aanpassingen erover; een taal zonder woordenboek (`pt-BR`) staat op het
 * Engels (VK). Spec 2026-09-26-taalcodes-taal-regio §3.1.
 */
export function dictionaryBase(locale: string): Locale {
  const shipped = new Set<string>(LOCALES)
  return (localeChain(locale).find((c) => shipped.has(c)) as Locale | undefined) ?? 'en-GB'
}

/**
 * De codes waarvan de tenantaanpassingen over het woordenboek gaan, in de volgorde waarin ze
 * erover komen: van de basis (`dictionaryBase`) naar de gevraagde code. Zo geldt een aanpassing
 * op nl-NL ook in nl-BE, en wint nl-BE's eigen aanpassing daarna. Tot 2026-09-28 kwamen alleen
 * de aanpassingen van precies de gevraagde code erover, en zag een nl-BE-gebruiker de
 * nl-NL-teksten van de tenant niet.
 */
export function overrideChain(locale: string): string[] {
  const chain = localeChain(locale)
  const base = chain.indexOf(dictionaryBase(locale))
  return (base === -1 ? chain : chain.slice(0, base + 1)).reverse()
}

/**
 * De taal waarop een EIGEN taal terugvalt voor een tekst die nog niet vertaald is (zijn
 * `dictionaryBase`); `null` voor een taal die we zelf meeleveren, want die valt nergens op terug.
 * Het scherm Vertalingen noemt hem in de status: nl-BE valt op het Nederlands, pl-PL op het
 * Engels.
 */
export function fallbackBase(locale: string): Locale | null {
  return (LOCALES as readonly string[]).includes(locale) ? null : dictionaryBase(locale)
}
